// src/auth/Register.jsx

import React, { useState } from "react";
import { validatePhone } from "../shared/utils/validatePhone";
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from "../app/context/AuthContext.jsx";
import { Link, useNavigate } from "react-router-dom";
import googleIcon from "../images/Google_Icons-09-512.webp";

/**
 * Register Component
 * ------------------
 * A user registration form that allows users to sign up using either manual input or Google Sign-In.
 * This form collects name, email, password, and phone number and includes a CAPTCHA verification step.
 * 
 * Features:
 * - Title and name inputs
 * - Formatted phone input with validation
 * - Email and password fields
 * - Google Sign-In option
 * - CAPTCHA protection
 * - Success and error message handling
 * - Tailwind CSS for styling and layout
 * 
 * Dependencies:
 * - `validatePhone` from `../../utils/validatePhone` for phone number validation
 * - `useAuth` from `../../context/AuthContext` for handling registration and Google login
 * - `react-google-recaptcha` for CAPTCHA
 * - `react-router-dom` for navigation
 * 
 * @component
 * @returns {JSX.Element} A fully functional registration form component.
 */
export const Register = () => {
  const { register, googleSignIn } = useAuth(); // Auth context methods
  const navigate = useNavigate(); // For programmatic navigation

  // Form field state variables
  const [passWord, setPassWord] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("Mr.");
  const [email, setEmail] = useState("");
  const [phoneIntl, setPhoneIntl] = useState("+1");
  const [phoneArea, setPhoneArea] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("");
  const [phoneLine, setPhoneLine] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);

  // Feedback message states
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Handles form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setErrorMessage("Please complete the CAPTCHA to continue.");
      return;
    }

    // Validate the phone number using utility
    const {
      isValid,
      fullNumber,
      errors: phoneErrors,
    } = validatePhone({
      intl: phoneIntl,
      area: phoneArea,
      prefix: phonePrefix,
      line: phoneLine,
    });

    if (!isValid) {
      setErrorMessage(phoneErrors.join(" | "));
      return;
    }

    try {
      // Attempt to register the user
      await register({
        email,
        password: passWord,
        firstName,
        lastName,
        title,
        phone: fullNumber,
      });

      // Show success message and reset form
      setSuccessMessage(
        "User registered! Please check your email to verify your account."
      );
      setErrorMessage("");

      setPassWord("");
      setFirstName("");
      setLastName("");
      setTitle("Mr.");
      setEmail("");
      setPhoneIntl("+1");
      setPhoneArea("");
      setPhonePrefix("");
      setPhoneLine("");
      setCaptchaToken(null);
    } catch (err) {
      console.error("Registration failed:", err);
      setErrorMessage(err.message || "Error registering user.");
      setSuccessMessage("");
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-base-200 px-4 py-12">
      {/* Registration form container */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">
          Register New Account
        </h2>

        {/* Success and error messages */}
        {successMessage && (
          <div className="text-green-600 mb-4">{successMessage}</div>
        )}
        {errorMessage && (
          <div className="text-red-600 mb-4">{errorMessage}</div>
        )}

        {/* Title and name fields */}
        <div className="grid grid-cols-12 gap-4 mb-4">
          <select
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="select select-bordered col-span-3"
            required
          >
            <option>Mr.</option>
            <option>Ms.</option>
            <option>Mrs.</option>
            <option>Dr.</option>
            <option>Prof.</option>
            <option>Rev.</option>
            <option>Other</option>
          </select>

          <input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="input input-bordered col-span-4"
            required
          />

          <input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="input input-bordered col-span-5"
            required
          />
        </div>

        {/* Email input */}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input input-bordered w-full mb-4"
          required
        />

        {/* Phone number input split into four fields */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="+1"
            value={phoneIntl}
            onChange={(e) => setPhoneIntl(e.target.value)}
            className="input input-bordered w-[15%] min-w-[55px]"
            maxLength={4}
            required
          />
          <input
            type="text"
            placeholder="Area"
            value={phoneArea}
            onChange={(e) => {
              setPhoneArea(e.target.value);
              if (e.target.value.length === 3) {
                document.getElementById("phonePrefix").focus(); // Auto-focus next input
              }
            }}
            className="input input-bordered w-[20%]"
            maxLength={3}
            required
          />
          <input
            type="text"
            id="phonePrefix"
            placeholder="Prefix"
            value={phonePrefix}
            onChange={(e) => {
              setPhonePrefix(e.target.value);
              if (e.target.value.length === 3) {
                document.getElementById("phoneLine").focus();
              }
            }}
            className="input input-bordered w-[20%]"
            maxLength={3}
            required
          />
          <input
            type="text"
            id="phoneLine"
            placeholder="Line"
            value={phoneLine}
            onChange={(e) => setPhoneLine(e.target.value)}
            className="input input-bordered w-[30%]"
            maxLength={4}
            required
          />
        </div>

        {/* Password input */}
        <input
          type="password"
          placeholder="Password"
          value={passWord}
          onChange={(e) => setPassWord(e.target.value)}
          className="input input-bordered w-full mb-6"
          required
        />

        {/* reCAPTCHA widget */}
        <ReCAPTCHA
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={(token) => setCaptchaToken(token)}
          className="mb-4"
        />

        {/* Manual registration button */}
        <button
          type="submit"
          className="btn btn-primary btn-block text-base mb-4"
        >
          Register
        </button>

        {/* Google Sign-In button */}
        <button
          type="button"
          onClick={async () => {
            try {
              await googleSignIn();
              navigate("/"); // Redirect on success
            } catch (err) {
              console.error("Google Sign-In failed:", err);
              setErrorMessage("Google Sign-In failed. Please try again.");
            }
          }}
          className="btn btn-outline btn-block mb-4"
        >
          {/* Google icon on both sides for aesthetic symmetry */}
          <img src={googleIcon} alt="Google logo" className="w-5 h-5" />
          <span className="flex-1 text-center">Sign up with Google</span>
          <img src={googleIcon} alt="Google logo" className="w-5 h-5" />
        </button>

        {/* Link to return to homepage */}
        <Link to="/" className="btn btn-outline btn-block">
          ← Back to Home
        </Link>
      </form>
    </div>
  );
};